'use client';

import { ResponsivePicture } from '@libs/shared-ui';
import { useAddToCart } from '@app/pdp/components/AddToCart';
import { useAddToCartDrawer, useProductDetails } from '@app/pdp/hooks';
import { mapFBTAddToCartItems } from '@app/pdp/utils';
import type { ProductCategory } from '@libs/algolia';
import {
    decodeEntityString,
    getDefaultCategories,
    getProductUrl,
    transformHitForProductTile,
} from '@libs/algolia';
import {
    Events,
    PageSectionAnalyticsTracking,
    TrackingParams,
    createItemsViewEvent,
    dataLayerPush,
    trackEEAddToCart,
    useAnalyticsContext,
    velocityTrackAddToCart,
} from '@libs/analytics';
import {
    type Product,
    NavigationListTypes,
    clsx,
    useBasketCount,
    usePageLayout,
    useDeviceType,
    useExperimentValue,
    ExperimentVersion,
} from '@libs/common-next';
import {
    Button,
    Checkbox,
    Icon,
    Price,
    TextPassage,
} from '@petsmart-ui/sparky';
import { useCallback, useState } from 'react';

import type { HandleAddToCart } from '@app/pdp/types';
import { AddToCartType, getInventoryStoreId } from '@libs/ocapi';

import { type Currency, DeliveryType, stringifyStoreId } from '@libs/core';
import './FrequentlyBoughtTogether.scss';

import type { FBTProduct, FrequentlyBoughtTogetherProps } from './types';

import { useAddFBTToCTCart, useFBT } from './hooks';
import { getAddToCartButtonLabel } from './libs';

const IMG_SIZE_FBT_PRODUCT = {
    width: 120,
    height: 120,
};

export default function FrequentlyBoughtTogether({
    supportedDevices,
}: Readonly<FrequentlyBoughtTogetherProps>) {
    const deviceType = useDeviceType();

    const {
        selectedStore: { store },
        ocapi,
        user,
    } = usePageLayout();
    const { getSearchStats } = useAnalyticsContext();
    const { product, selectedDeliveryMethod, handleAddToCart } =
        useProductDetails();
    const experimentValue = useExperimentValue();
    /* Handle Commercetools add to cart */
    const { handleAddToCTCart } = useAddFBTToCTCart();
    const isCommercetoolsEnabled =
        experimentValue === ExperimentVersion.FULL_SOLUTION;

    const { basketCount, handleBasketCount } = useBasketCount();
    const { openModalDrawerAddedToCart } = useAddToCartDrawer() ?? {};

    const sessionCustomer = ocapi.data?.session.customer;
    const customerId = isCommercetoolsEnabled
        ? String(user?.customerprofileid ?? '0')
        : sessionCustomer?.customer_id ?? '0';
    const { recommendedProducts, handleSelectProduct } = useFBT({
        product,
        customerId,
        storeId: store?.StoreNumber.toString(),
        isEnabled:
            !ocapi.isLoading && selectedDeliveryMethod === DeliveryType.BOPIS,
    });
    const MIN_RECOMMENDATIONS = 3;

    const handleAddToCartWrapper: HandleAddToCart = async (
        productData,
        basketId
    ) => {
        const response = await handleAddToCart(productData, basketId);

        if (!response || 'fault' in response) {
            return response;
        }

        const selectedProducts = recommendedProducts.filter(p => p.isSelected);

        const result = response?.product_items?.map(
            productItem => productItem.product_id
        );

        const addedProducts = selectedProducts.filter(sentProduct =>
            result.includes(sentProduct.objectID)
        );

        const addedProductIds = selectedProducts.map(p => p.objectID);

        handleBasketCount?.(response.c_cartCount ?? 0);
        openModalDrawerAddedToCart?.(
            response.product_items.filter(p =>
                addedProductIds.includes(p.product_id)
            )
        );

        const { searchTerm } = getSearchStats();
        velocityTrackAddToCart(
            product,
            response.currency as Currency,
            NavigationListTypes.STANDARD_PDP,
            'yes',
            mapFBTAddToCartItems(
                product,
                addedProducts,
                store,
                basketCount + 1,
                selectedDeliveryMethod,
                searchTerm
            )
        );

        const eventProductParameter = productData.map(sentProduct => {
            const algoliaHit = recommendedProducts.find(
                p => p.objectID === sentProduct.product_id
            ) as FBTProduct;

            return {
                algoliaHit,
                parameters: {
                    quantity: sentProduct.quantity ?? 1,
                    fulfillmentMethod: selectedDeliveryMethod as DeliveryType,
                    type: TrackingParams.STANDARD_PDP,
                    autoshipFrequency: sentProduct.c_RecurringShipmentFrequency,
                    storeID: sentProduct.c_fromStoreId,
                },
            };
        });

        // Yes, the event supports a product array, but SFCC sends them individually anyway
        // and we are aiming to imitate SFCC events for a seamless transition.
        eventProductParameter.forEach(eventProduct => {
            trackEEAddToCart([eventProduct], AddToCartType.RECS);
        });

        return response;
    };

    const { handleAddToCartAction: addToCart } = useAddToCart(
        handleAddToCartWrapper
    );

    const [sentDatalayer, setSentDatalayer] = useState<boolean>(false);

    const basketId = ocapi.data?.basket?.basket_id;
    const addToCartData = {
        quantity:
            recommendedProducts.filter(fbtProduct => fbtProduct.isSelected)
                .length ?? 0,
        subtotal: recommendedProducts.reduce(
            (total, fbtProduct) =>
                total +
                (fbtProduct.isSelected ? fbtProduct.price?.number ?? 0.0 : 0.0),
            0
        ),
    };

    const isAllProductsNotSelected = addToCartData.quantity === 0;
    const isAllProductsOutOfStock = recommendedProducts.every(
        fbtProduct => !fbtProduct.in_stock
    );

    // State of the process adding FBT products to CT cart
    const [isPendingAddToCTCart, setIsPendingAddToCTCart] =
        useState<boolean>(false);
    const handleAddToCartClick = useCallback(async () => {
        if (
            isPendingAddToCTCart ||
            isAllProductsOutOfStock ||
            isAllProductsNotSelected
        ) {
            return;
        }

        const selectedProducts = recommendedProducts.filter(
            ftbProduct => ftbProduct.isSelected
        );

        /**
         * If commercetools flag enabled
         *
         * Adding `recommendedProducts.length` to the check does not make sense
         * Because the "Add to pickup" button doesn't appear if there are no products selected
         */
        if (isCommercetoolsEnabled && store?.objectID) {
            handleAddToCTCart({
                products: selectedProducts,
                storeId: store.objectID,
                onStartCallback: () => setIsPendingAddToCTCart(true),
                onEndCallback: () => setIsPendingAddToCTCart(false),
            });
            return;
        }

        // SFCC add to cart
        if (recommendedProducts.length && basketId) {
            const productFiltered = selectedProducts.map(ftbProduct => ({
                product_id: ftbProduct.objectID,
                quantity: 1,
                inventory_id: getInventoryStoreId(store?.StoreNumber),
                c_addToCartType: AddToCartType.RECS,
                c_fromStoreId: stringifyStoreId(store?.StoreNumber),
                c_isScheduleDelivLineItem: false,
            }));
            await addToCart?.(productFiltered);
        }
    }, [
        isPendingAddToCTCart,
        isAllProductsOutOfStock,
        isAllProductsNotSelected,
        recommendedProducts,
        isCommercetoolsEnabled,
        store?.objectID,
        store?.StoreNumber,
        basketId,
        handleAddToCTCart,
        addToCart,
    ]);

    if (!supportedDevices?.includes(deviceType)) {
        return null;
    }

    if (!(store !== null && !product.isLivePet)) {
        return null;
    }

    if (recommendedProducts.length < MIN_RECOMMENDATIONS) {
        return null;
    }

    const setFindingMethod = () => {
        window.sessionStorage.pdpFindingMethod =
            TrackingParams.RECOMMENDATION_REFERER;
    };

    return (
        <div
            data-testid="test-frequently-bought-together"
            title="Frequently bought together"
            className="frequently-bought-together sparky-u-margin-top-lg"
        >
            <div>
                <h2 className="frequently-bought-together__title">
                    Frequently bought together
                </h2>
            </div>
            <div className="frequently-bought-together__images-total">
                {addToCartData.quantity > 0 && (
                    <>
                        <ul
                            className="frequently-bought-together__list"
                            key={recommendedProducts[0].id}
                        >
                            {recommendedProducts
                                .filter(fbtProduct => fbtProduct.isSelected)
                                .map((fbtProduct, index, selectedProducts) => {
                                    const hasMore =
                                        index + 1 < selectedProducts.length;

                                    return (
                                        <li
                                            key={fbtProduct.id.toString()}
                                            className="frequently-bought-together__item"
                                            data-testid="test-product-recommendations-visibility"
                                        >
                                            <a
                                                onClick={setFindingMethod}
                                                href={getProductUrl(fbtProduct)}
                                            >
                                                <ResponsivePicture
                                                    className="frequently-bought-together__image"
                                                    src={
                                                        fbtProduct.images
                                                            ?.small ?? ''
                                                    }
                                                    alt={`${fbtProduct.name} image`}
                                                    size={IMG_SIZE_FBT_PRODUCT}
                                                />
                                            </a>
                                            {hasMore && (
                                                <Icon
                                                    className="c-image-list__icon frequently-bought-together__plus-sign"
                                                    name="plus"
                                                />
                                            )}
                                        </li>
                                    );
                                })}
                        </ul>
                        <div className="frequently-bought-together__add-to-cart">
                            <TextPassage className="frequently-bought-together__subtotal">
                                <span className="sparky-u-margin-right-xs">
                                    Subtotal:
                                </span>
                                <Price
                                    className="sparky-u-display-inline sparky-u-margin-right-xs"
                                    variant="sale"
                                    values={[addToCartData.subtotal]}
                                />
                                ({addToCartData.quantity}
                                {addToCartData.quantity > 1
                                    ? ' items'
                                    : ' item'}
                                )
                            </TextPassage>

                            {/* Add to pickup button */}
                            <Button
                                className="frequently-bought-together__add-to-cart-button"
                                data-testid="test-fbt-add-to-cart-button"
                                onClick={handleAddToCartClick}
                                loading={isPendingAddToCTCart}
                                disabled={
                                    isAllProductsOutOfStock ||
                                    isAllProductsNotSelected
                                }
                                text={getAddToCartButtonLabel({
                                    quantity: addToCartData.quantity,
                                    productsCount: recommendedProducts.length,
                                })}
                            />
                        </div>
                    </>
                )}
                {!isAllProductsOutOfStock && isAllProductsNotSelected && (
                    <p
                        className="sparky-c-text-passage frequently-bought-together__text-passage"
                        data-testid="test-fbt-no-selected"
                    >
                        Choose items to buy together
                    </p>
                )}
                {isAllProductsOutOfStock && isAllProductsNotSelected && (
                    <p className="sparky-c-text-passage frequently-bought-together__text-passage">
                        These frequently bought items are out of stock in your
                        area
                    </p>
                )}
            </div>

            <PageSectionAnalyticsTracking
                sectionId="product-fbt"
                eventCallback={() => {
                    dataLayerPush({
                        event: Events.VIEW_ITEM_LIST,
                        ...createItemsViewEvent(
                            recommendedProducts.map(
                                (fbtProduct: FBTProduct) =>
                                    transformHitForProductTile(
                                        fbtProduct,
                                        true
                                    ) as Product
                            ),
                            'product recommendations',
                            'product recommendation',
                            getDefaultCategories(product).map(
                                (p: ProductCategory) => p.name.default
                            ),
                            undefined
                        ),
                    });
                }}
                enabled={!sentDatalayer}
                OnIntersect={() => setSentDatalayer(true)}
            >
                <div
                    className="frequently-bought-together__fbt-product-list"
                    key={recommendedProducts?.[0]?.id}
                >
                    {recommendedProducts.map((fbtProduct, index) => {
                        const isMasterProduct = index === 0;
                        const priceStyleVariant = fbtProduct.price?.formatted
                            .strikethrough
                            ? 'sale'
                            : 'primary';

                        const isProductDisabled = !fbtProduct.in_stock;
                        const isProductChecked =
                            Boolean(fbtProduct.isSelected) &&
                            Boolean(fbtProduct.in_stock);

                        return (
                            <div
                                className="frequently-bought-together__fbt-product-container-items"
                                key={fbtProduct.id}
                            >
                                <Checkbox
                                    data-checked={isProductChecked}
                                    value={fbtProduct.objectID}
                                    onChange={event => {
                                        handleSelectProduct(event, index);
                                    }}
                                    checked={isProductChecked}
                                    disabled={isProductDisabled}
                                    data-testid={`fbt-checkbox-${fbtProduct.id}`}
                                />
                                {isMasterProduct ? (
                                    <div
                                        className={clsx(
                                            isProductDisabled &&
                                                'sparky-c-text-link--disabled',
                                            'frequently-bought-together__fbt-product-container-items'
                                        )}
                                    >
                                        <div className="frequently-bought-together__fbt-product-container-items-label">
                                            <b className="sparky-u-margin-right-xs">
                                                This item:
                                            </b>
                                            <span>
                                                {decodeEntityString(
                                                    fbtProduct.name ?? ''
                                                )}
                                            </span>
                                        </div>
                                        <Price
                                            className="sparky-u-display-inline"
                                            variant={priceStyleVariant}
                                            values={[
                                                fbtProduct.price?.number ?? 0.0,
                                            ]}
                                        />
                                    </div>
                                ) : (
                                    <>
                                        <a
                                            onClick={setFindingMethod}
                                            className="sparky-c-text-link"
                                            href={getProductUrl(fbtProduct)}
                                        >
                                            {decodeEntityString(
                                                fbtProduct.name ?? ''
                                            )}
                                        </a>
                                        <Price
                                            className="sparky-u-display-inline"
                                            variant={priceStyleVariant}
                                            values={[
                                                fbtProduct.price?.number ?? 0.0,
                                            ]}
                                        />
                                    </>
                                )}
                            </div>
                        );
                    })}
                </div>
            </PageSectionAnalyticsTracking>
        </div>
    );
}
